import React, { useEffect, useState, useRef } from 'react';
import styles from '../capitalExpenditure.module.scss';
import { parseNumericValue } from '../utils/numberUtils';
import { exportDetailedSummaryTableToExcel } from '../utils/excelExport';
import { printSummaryTableAsPDF, generateSummaryTablePDF } from '../utils/pdfExport';
import CapitalExpenditureQ1Report from './CapitalExpenditureQ1Report';
import CapitalExpenditureQ2Report from './CapitalExpenditureQ2Report';
import CapitalExpenditureQ3Report from './CapitalExpenditureQ3Report';
import CapitalExpenditureQ4Report from './CapitalExpenditureQ4Report';
import CapitalExpenditureAnnualTotalsReport from './CapitalExpenditureAnnualTotalsReport';
import BudgetReport from './BudgetReport';

const CapitalExpenditureDataView = ({
  existingData,
  filteredData,
  selectedDataForEdit,
  viewMode,
  loading,
  showFilters,
  filters,
  handleView,
  handleEdit,
  handleDelete,
  handleUpdate,
  handleExportExcel,
  handleExportFilteredData,
  handlePrint,
  setShowFilters,
  setFilters,
  setSelectedDataForEdit,
  setViewMode,
  onRefreshData,
  setFilteredData,
  selectedOffice,
  fiscalYears = [],
  selectedFiscalYear,
  masterItems = [],
  fetchDetailedDataForReports,
  fetchBudgetReportData,
  handleBudgetView,
  handleBudgetEdit,
  handleBudgetDownload,
  handleBudgetSave
}) => {
  const [showSummaryReport, setShowSummaryReport] = useState(false);
  const [showDetailedReport, setShowDetailedReport] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showQ1Report, setShowQ1Report] = useState(false);
  const [showQ2Report, setShowQ2Report] = useState(false);
  const [showQ3Report, setShowQ3Report] = useState(false);
  const [showQ4Report, setShowQ4Report] = useState(false);
  const [showBudgetReport, setShowBudgetReport] = useState(false);
  const [showAnnualTotalsReport, setShowAnnualTotalsReport] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [detailedData, setDetailedData] = useState([]);
  const [loadingDetailedData, setLoadingDetailedData] = useState(false);
  const [loadingBudgetData, setLoadingBudgetData] = useState(false);
  const [budgetItems, setBudgetItems] = useState([]);
  const [hasAppliedFilters, setHasAppliedFilters] = useState(false);
  const [budgetActionLoadingId, setBudgetActionLoadingId] = useState(null);
  const summaryTableRef = useRef(null);

  // Ensure we always have arrays to work with
  const safeExistingData = Array.isArray(existingData) ? existingData : [];
  const safeFilteredData = Array.isArray(filteredData) ? filteredData : [];
  const displayData = hasAppliedFilters ? safeFilteredData : safeExistingData;

  // Reset filtered data when existing data changes
  useEffect(() => {
    if (existingData && Array.isArray(existingData) && existingData.length > 0) {
      setFilteredData([]);
      setHasAppliedFilters(false);
    }
  }, [existingData, setFilteredData]);

  const applyFilters = () => {
    const filtered = safeExistingData.filter((item) => {
      const institutionMatch = !filters.institution ||
        (item.institution_name || '').toLowerCase().includes(filters.institution.toLowerCase());

      const fiscalYearMatch = !filters.fiscalYear ||
        (item.fiscal_year_display || '').toLowerCase().includes(filters.fiscalYear.toLowerCase());

      const statusMatch = !filters.status || item.status === filters.status;

      let dateFromMatch = true;
      let dateToMatch = true;
      const createdAt = item.created_at ? new Date(item.created_at) : null;

      if (filters.dateFrom && createdAt) {
        const fromDate = new Date(`${filters.dateFrom}T00:00:00`);
        dateFromMatch = createdAt >= fromDate;
      }

      if (filters.dateTo && createdAt) {
        const toDate = new Date(`${filters.dateTo}T23:59:59`);
        dateToMatch = createdAt <= toDate;
      }

      return institutionMatch && fiscalYearMatch && statusMatch && dateFromMatch && dateToMatch;
    });

    setFilteredData(filtered);
    setHasAppliedFilters(true);
  };

  const clearFilters = () => {
    setFilters({
      institution: '',
      fiscalYear: '',
      dateFrom: '',
      dateTo: '',
      itemType: '',
      minBudget: '',
      maxBudget: '',
      status: ''
    });
    setFilteredData([]);
    setHasAppliedFilters(false);
  };

  useEffect(() => {
    if (!showBudgetReport || !fetchBudgetReportData) {
      return;
    }

    const loadBudgetData = async () => {
      try {
        setLoadingBudgetData(true);
        const data = await fetchBudgetReportData(hasAppliedFilters ? filters : {});
        setBudgetItems(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error loading budget report data:', error);
        setBudgetItems([]);
      } finally {
        setLoadingBudgetData(false);
      }
    };

    loadBudgetData();
  }, [showBudgetReport, fetchBudgetReportData, filters, hasAppliedFilters]);

  // Fetch detailed data when detailed report is shown
  useEffect(() => {
    if (showDetailedReport && fetchDetailedDataForReports && existingData.length > 0) {
      setLoadingDetailedData(true);
      fetchDetailedDataForReports()
        .then(data => {
          console.log('Setting detailed data:', data);
          setDetailedData(data);
        })
        .catch(error => {
          console.error('Error fetching detailed data:', error);
          setDetailedData(existingData); // Fallback to existing data
        })
        .finally(() => {
          setLoadingDetailedData(false);
        });
    }
  }, [showDetailedReport, fetchDetailedDataForReports, existingData]);

  const runBudgetAction = async (budgetSubmissionId, actionHandler) => {
    if (!budgetSubmissionId || typeof actionHandler !== 'function') {
      return;
    }

    try {
      setBudgetActionLoadingId(budgetSubmissionId);
      const result = await actionHandler(budgetSubmissionId);
      return result;
    } catch (error) {
      console.error('Budget action failed:', error);
      return null;
    } finally {
      setBudgetActionLoadingId(null);
    }
  };

  // Handle select all checkbox
  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedItems(displayData.map(item => item.id));
    } else {
      setSelectedItems([]);
    }
  };

  // Handle individual item selection
  const handleItemSelect = (itemId, checked) => {
    if (checked) {
      setSelectedItems(prev => [...prev, itemId]);
    } else {
      setSelectedItems(prev => prev.filter(id => id !== itemId));
    }
  };

  // Handle Excel download for detailed summary table
  const handleDownloadExcel = async () => {
    try {
      // Process the data similar to how it's displayed in the table
      let allExpenditureItems = [];
      
      // Use detailed data if available for better reports
      const dataForExport = detailedData.length > 0 ? detailedData : existingData;
      
      if (Array.isArray(dataForExport) && dataForExport.length > 0) {
        const allCapitalItems = [];
        const allOperationalItems = [];
        
        dataForExport.forEach(expenditure => {
          if (expenditure.expenditure_items && Array.isArray(expenditure.expenditure_items)) {
            const capitalItems = expenditure.expenditure_items
              .filter(item => item.item_type === 'capital' || 
                             (item.code_number && item.code_number.startsWith('01.')))
              .map(item => ({
                ...item,
                institution_name: expenditure.institution_name || selectedOffice?.name || 'केन्द्रीय कार्यालय',
                item_type: 'capital'
              }));
            
            const operationalItems = expenditure.expenditure_items
              .filter(item => item.item_type === 'operational' || 
                             (item.code_number && item.code_number.startsWith('02.')))
              .map(item => ({
                ...item,
                institution_name: expenditure.institution_name || selectedOffice?.name || 'केन्द्रीय कार्यालय',
                item_type: 'operational'
              }));
            
            allCapitalItems.push(...capitalItems);
            allOperationalItems.push(...operationalItems);
          }
        });
        
        allExpenditureItems = [...allCapitalItems, ...allOperationalItems];
      }
      
      // Get unique institutions
      const institutions = [...new Set(allExpenditureItems.map(item => item.institution_name).filter(Boolean))];
      
      if (allExpenditureItems.length === 0) {
        alert('कुनै डाटा फेला परेन। कृपया पहिले डाटा लोड गर्नुहोस्।');
        return;
      }
      
      const result = await exportDetailedSummaryTableToExcel(allExpenditureItems, institutions, 'खर्च_सारांश_तालिका');
      
      if (result.success) {
        alert(`Excel फाइल सफलतापूर्वक डाउनलोड भयो: ${result.fileName}`);
      } else {
        alert(`Excel डाउनलोडमा समस्या भयो: ${result.error}`);
      }
    } catch (error) {
      console.error('Error downloading Excel:', error);
      alert('Excel डाउनलोडमा समस्या भयो।');
    }
  };

  // Handle PDF download for detailed summary table
  const handleDownloadPDF = async () => {
    try {
      // Process the data similar to how it's displayed in the table
      let allExpenditureItems = [];
      
      // Use detailed data if available for better reports
      const dataForExport = detailedData.length > 0 ? detailedData : existingData;
      
      if (Array.isArray(dataForExport) && dataForExport.length > 0) {
        const allCapitalItems = [];
        const allOperationalItems = [];
        
        dataForExport.forEach(expenditure => {
          if (expenditure.expenditure_items && Array.isArray(expenditure.expenditure_items)) {
            const capitalItems = expenditure.expenditure_items
              .filter(item => item.item_type === 'capital' || 
                             (item.code_number && item.code_number.startsWith('01.')))
              .map(item => ({
                ...item,
                institution_name: expenditure.institution_name || selectedOffice?.name || 'केन्द्रीय कार्यालय',
                item_type: 'capital'
              }));
            
            const operationalItems = expenditure.expenditure_items
              .filter(item => item.item_type === 'operational' || 
                             (item.code_number && item.code_number.startsWith('02.')))
              .map(item => ({
                ...item,
                institution_name: expenditure.institution_name || selectedOffice?.name || 'केन्द्रीय कार्यालय',
                item_type: 'operational'
              }));
            
            allCapitalItems.push(...capitalItems);
            allOperationalItems.push(...operationalItems);
          }
        });
        
        allExpenditureItems = [...allCapitalItems, ...allOperationalItems];
      }
      
      // Get unique institutions
      const institutions = [...new Set(allExpenditureItems.map(item => item.institution_name).filter(Boolean))];
      
      if (allExpenditureItems.length === 0) {
        alert('कुनै डाटा फेला परेन। कृपया पहिले डाटा लोड गर्नुहोस्।');
        return;
      }
      
      const result = await generateSummaryTablePDF(allExpenditureItems, institutions, 'खर्च_सारांश_तालिका');
      
      if (result.success) {
        alert(`PDF फाइल सफलतापूर्वक डाउनलोड भयो: ${result.fileName}`);
      } else {
        alert(`PDF डाउनलोडमा समस्या भयो: ${result.error}`);
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('PDF डाउनलोडमा समस्या भयो।');
    }
  };

  // Handle print for detailed summary table
  const handlePrintSummaryTable = async () => {
    try {
      if (summaryTableRef.current) {
        const result = await printSummaryTableAsPDF(summaryTableRef.current, 'खर्च_सारांश_तालिका');
        
        if (result.success) {
          console.log('Print window opened successfully');
        } else {
          alert(`प्रिन्टमा समस्या भयो: ${result.error}`);
        }
      } else {
        alert('तालिका फेला परेन। कृपया पहिले विस्तृत रिपोर्ट खोल्नुहोस्।');
      }
    } catch (error) {
      console.error('Error printing:', error);
      alert('प्रिन्टमा समस्या भयो।');
    }
  };

  // Bulk delete function
  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) {
      alert('कृपया मेटाउन चाहने आइटमहरू छान्नुहोस्');
      return;
    }
    
    const confirmDelete = window.confirm(`तपाईंले ${selectedItems.length} वटा आइटमहरू मेटाउन चाहनुहुन्छ? यो कार्य फिर्ता गर्न सकिँदैन।`);
    
    if (confirmDelete) {
      try {
        // Call the delete function for each selected item
        for (const itemId of selectedItems) {
          await handleDelete({ id: itemId });
        }
        
        // Clear selection after deletion
        setSelectedItems([]);
        setSelectAll(false);
        
        alert(`${selectedItems.length} वटा आइटमहरू सफलतापूर्वक मेटाइयो`);
      } catch (error) {
        console.error('Bulk delete error:', error);
        alert('केही आइटमहरू मेटाउन असफल। कृपया पुनः प्रयास गर्नुहोस्।');
      }
    }
  };

  return (
    <div className={styles.dataViewContainer}>
      <div className={styles.dataViewYearBar}>
        <span>आर्थिक वर्ष:</span>
        <strong>{selectedFiscalYear?.name || '—'}</strong>
        {selectedFiscalYear?.is_current ? <em>(चालु वर्षको डाटा)</em> : <em>(अघिल्लो वर्षको डाटा)</em>}
      </div>
      {/* Filters Section */}
      <div className={styles.filtersSection}>
        <div className={styles.filterActions}>
          <button
            className={styles.filterToggleButton}
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? 'फिल्टर लुकाउनुहोस्' : 'फिल्टर देखाउनुहोस्'}
          </button>

          <button
            className={styles.refreshButton}
            onClick={onRefreshData}
            disabled={loading}
          >
            {loading ? 'लोड हुँदैछ...' : 'डाटा रिफ्रेश गर्नुहोस्'}
          </button>

          <button
            className={styles.excelButton}
            onClick={handleExportFilteredData}
            disabled={loading || displayData.length === 0}
            title="फिल्टर गरिएको डाटा Excel मा डाउनलोड गर्नुहोस्"
          >
            📊 फिल्टर गरिएको डाटा डाउनलोड
          </button>

          <button
            className={`${styles.tabButton} ${!showSummaryReport && !showDetailedReport && !showHelp && !showQ1Report && !showQ2Report && !showQ3Report && !showQ4Report && !showBudgetReport && !showAnnualTotalsReport ? styles.activeTab : ''}`}
            onClick={() => {
              setShowSummaryReport(false);
              setShowDetailedReport(false);
              setShowHelp(false);
              setShowQ1Report(false);
              setShowQ2Report(false);
              setShowQ3Report(false);
              setShowQ4Report(false);
              setShowBudgetReport(false);
              setShowAnnualTotalsReport(false);
            }}
            title="डाटा तालिका देखाउनुहोस्"
          >
            📋 डाटा तालिका
          </button>

          {/* सारांश रिपोर्ट tab removed as per request */}

          <button
            className={`${styles.tabButton} ${showHelp ? styles.activeTab : ''}`}
            onClick={() => {
              setShowSummaryReport(false);
              setShowDetailedReport(false);
              setShowHelp(true);
              setShowQ1Report(false);
              setShowQ2Report(false);
              setShowQ3Report(false);
              setShowQ4Report(false);
              setShowBudgetReport(false);
              setShowAnnualTotalsReport(false);
            }}
            title="सहायता देखाउनुहोस्"
          >
            ℹ️ सहायता
          </button>

          <button
            className={`${styles.tabButton} ${showQ1Report ? styles.activeTab : ''}`}
            onClick={() => {
              setShowSummaryReport(false);
              setShowDetailedReport(false);
              setShowHelp(false);
              setShowQ1Report(true);
              setShowQ2Report(false);
              setShowQ3Report(false);
              setShowQ4Report(false);
              setShowBudgetReport(false);
              setShowAnnualTotalsReport(false);
            }}
            title="प्रथम त्रैमासिक रिपोर्ट"
          >
            प्रथम त्रैमासिक
          </button>

          <button
            className={`${styles.tabButton} ${showQ2Report ? styles.activeTab : ''}`}
            onClick={() => {
              setShowSummaryReport(false);
              setShowDetailedReport(false);
              setShowHelp(false);
              setShowQ1Report(false);
              setShowQ2Report(true);
              setShowQ3Report(false);
              setShowQ4Report(false);
              setShowBudgetReport(false);
              setShowAnnualTotalsReport(false);
            }}
            title="दोस्रो त्रैमासिक रिपोर्ट"
          >
            दोस्रो त्रैमासिक
          </button>

          <button
            className={`${styles.tabButton} ${showQ3Report ? styles.activeTab : ''}`}
            onClick={() => {
              setShowSummaryReport(false);
              setShowDetailedReport(false);
              setShowHelp(false);
              setShowQ1Report(false);
              setShowQ2Report(false);
              setShowQ3Report(true);
              setShowQ4Report(false);
              setShowBudgetReport(false);
              setShowAnnualTotalsReport(false);
            }}
            title="तेस्रो त्रैमासिक रिपोर्ट"
          >
            तेस्रो त्रैमासिक
          </button>

          <button
            className={`${styles.tabButton} ${showQ4Report ? styles.activeTab : ''}`}
            onClick={() => {
              setShowSummaryReport(false);
              setShowDetailedReport(false);
              setShowHelp(false);
              setShowQ1Report(false);
              setShowQ2Report(false);
              setShowQ3Report(false);
              setShowQ4Report(true);
              setShowBudgetReport(false);
              setShowAnnualTotalsReport(false);
            }}
            title="चौथो त्रैमासिक रिपोर्ट"
          >
            चौथो त्रैमासिक
          </button>

          <button
            className={`${styles.tabButton} ${showBudgetReport ? styles.activeTab : ''}`}
            onClick={() => {
              setShowSummaryReport(false);
              setShowDetailedReport(false);
              setShowHelp(false);
              setShowQ1Report(false);
              setShowQ2Report(false);
              setShowQ3Report(false);
              setShowQ4Report(false);
              setShowBudgetReport(true);
              setShowAnnualTotalsReport(false);
            }}
            title="बजेट रिपोर्ट"
          >
            बजेट
          </button>

          <button
            className={`${styles.tabButton} ${showAnnualTotalsReport ? styles.activeTab : ''}`}
            onClick={() => {
              setShowSummaryReport(false);
              setShowDetailedReport(false);
              setShowHelp(false);
              setShowQ1Report(false);
              setShowQ2Report(false);
              setShowQ3Report(false);
              setShowQ4Report(false);
              setShowBudgetReport(false);
              setShowAnnualTotalsReport(true);
            }}
            title="कुल बजेट विवरण रिपोर्ट"
          >
            कुल बजेट विवरण
          </button>

          <button
            className={`${styles.tabButton} ${showDetailedReport ? styles.activeTab : ''}`}
            onClick={() => {
              setShowSummaryReport(false);
              setShowDetailedReport(true);
              setShowHelp(false);
              setShowQ1Report(false);
              setShowQ2Report(false);
              setShowQ3Report(false);
              setShowQ4Report(false);
              setShowBudgetReport(false);
              setShowAnnualTotalsReport(false);
            }}
            title="विस्तृत रिपोर्ट देखाउनुहोस्"
          >
            📋 विस्तृत रिपोर्ट
          </button>

          {/* Bulk Delete Button */}
          {selectedItems.length > 0 && (
            <button
              className={styles.bulkDeleteButton}
              onClick={handleBulkDelete}
              title={`${selectedItems.length} वटा आइटमहरू मेटाउनुहोस्`}
            >
              🗑️ बल्क डिलिट ({selectedItems.length})
            </button>
          )}
        </div>

        {showFilters && (
          <div className={styles.filtersContainer}>
            <div className={styles.filterRow}>
              <div className={styles.filterGroup}>
                <label htmlFor="filterInstitution">संस्था</label>
                <input
                  id="filterInstitution"
                  className={styles.filterInput}
                  type="text"
                  placeholder="संस्था नाम"
                  value={filters.institution}
                  onChange={(e) => setFilters({ ...filters, institution: e.target.value })}
                />
              </div>
              <div className={styles.filterGroup}>
                <label htmlFor="filterFiscalYear">आर्थिक वर्ष</label>
                <input
                  id="filterFiscalYear"
                  className={styles.filterInput}
                  type="text"
                  value={selectedFiscalYear?.name || filters.fiscalYear || ''}
                  readOnly
                  title="आर्थिक वर्ष माथिबाट परिवर्तन गर्नुहोस्"
                />
              </div>
              <div className={styles.filterGroup}>
                <label htmlFor="filterStatus">स्थिति</label>
                <select
                  id="filterStatus"
                  className={styles.filterSelect}
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                >
                  <option value="">सबै</option>
                  <option value="draft">मस्यौदा</option>
                  <option value="submitted">पेश गरिएको</option>
                  <option value="approved">मन्जुर</option>
                  <option value="rejected">अस्वीकृत</option>
                  <option value="completed">पूर्ण</option>
                </select>
              </div>
            </div>
            <div className={styles.filterRow}>
              <div className={styles.filterGroup}>
                <label htmlFor="filterDateFrom">मिति बाट</label>
                <input
                  id="filterDateFrom"
                  className={styles.filterInput}
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                />
              </div>
              <div className={styles.filterGroup}>
                <label htmlFor="filterDateTo">मिति सम्म</label>
                <input
                  id="filterDateTo"
                  className={styles.filterInput}
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                />
              </div>
              <div className={styles.filterGroup} />
            </div>
            <div className={styles.filterActions}>
              <button
                type="button"
                className={styles.applyFilterButton}
                onClick={applyFilters}
              >
                फिल्टर लागू गर्नुहोस्
              </button>
              <button
                type="button"
                className={styles.clearFilterButton}
                onClick={clearFilters}
              >
                फिल्टर हटाउनुहोस्
              </button>
            </div>
          </div>
        )}
      </div>

      {/* सारांश रिपोर्ट section removed as per request */}

      {showQ1Report && (
        <CapitalExpenditureQ1Report
          existingData={existingData}
          selectedOffice={selectedOffice}
          fetchDetailedDataForReports={fetchDetailedDataForReports}
          displayData={displayData}
        />
      )}

      {showQ2Report && (
        <CapitalExpenditureQ2Report
          existingData={existingData}
          selectedOffice={selectedOffice}
          fetchDetailedDataForReports={fetchDetailedDataForReports}
          displayData={displayData}
        />
      )}

      {showQ3Report && (
        <CapitalExpenditureQ3Report
          existingData={existingData}
          selectedOffice={selectedOffice}
          fetchDetailedDataForReports={fetchDetailedDataForReports}
          displayData={displayData}
        />
      )}

      {showQ4Report && (
        <CapitalExpenditureQ4Report
          existingData={existingData}
          selectedOffice={selectedOffice}
          fetchDetailedDataForReports={fetchDetailedDataForReports}
          displayData={displayData}
        />
      )}

      {showBudgetReport && (
        <BudgetReport
          loading={loadingBudgetData}
          budgetItems={budgetItems}
          masterItems={masterItems}
          filters={hasAppliedFilters ? filters : {}}
          onViewBudget={(budgetSubmissionId) => runBudgetAction(budgetSubmissionId, handleBudgetView)}
          onEditBudget={(budgetSubmissionId) => runBudgetAction(budgetSubmissionId, handleBudgetEdit)}
          onDownloadBudget={(budgetSubmissionId) => runBudgetAction(budgetSubmissionId, handleBudgetDownload)}
          onSaveBudget={handleBudgetSave}
          onRefreshBudgetList={async () => {
            if (!fetchBudgetReportData) {
              return;
            }

            try {
              setLoadingBudgetData(true);
              const data = await fetchBudgetReportData(hasAppliedFilters ? filters : {});
              setBudgetItems(Array.isArray(data) ? data : []);
            } catch (error) {
              console.error('Error refreshing budget report data:', error);
            } finally {
              setLoadingBudgetData(false);
            }
          }}
          budgetActionLoadingId={budgetActionLoadingId}
        />
      )}
      {showAnnualTotalsReport && (
        <CapitalExpenditureAnnualTotalsReport
          displayData={displayData}
          existingData={existingData}
          selectedOffice={selectedOffice}
          fetchDetailedDataForReports={fetchDetailedDataForReports}
        />
      )}



      {/* Detailed Report Section */}
      {showDetailedReport && (
        <div className={styles.detailedReportSection}>
          <div className={styles.detailedHeader}>
            <h3>📋 विस्तृत रिपोर्ट</h3>
            <p>Summary.xlsx जस्तै फरमाटमा खर्च आइटमहरूको विस्तृत विवरण</p>
          </div>

           {/* Detailed Report Tabs */}
           <div className={styles.detailedReportTabs}>
             <button 
               className={`${styles.detailedTabButton} ${styles.activeDetailedTab}`}
               title="विस्तृत रिपोर्ट देखाउनुहोस्"
             >
               📋 विस्तृत रिपोर्ट
             </button>
             
             <button 
               className={styles.detailedTabButton}
               onClick={handleDownloadExcel}
               title="Excel मा डाउनलोड गर्नुहोस्"
             >
               📊 Excel डाउनलोड
             </button>
             
             <button 
               className={styles.detailedTabButton}
               onClick={handlePrintSummaryTable}
               title="प्रिन्ट गर्नुहोस्"
             >
               🖨️ प्रिन्ट
             </button>
           </div>

           {/* Summary.xlsx Format Table */}
           <div className={styles.summaryExcelTableContainer}>
             <h4 style={{ textAlign: 'left' }}>खर्च सारांश तालिका (नयाँ डाटा थप्नुहोस् मोडेलबाट)</h4>
             {loadingDetailedData ? (
               <div className={styles.loading}>
                 <p>विस्तृत डाटा लोड हुँदैछ...</p>
               </div>
             ) : (
             <div className={styles.tableWrapper}>
               <table className={styles.summaryExcelTable} ref={summaryTableRef}>
                 <thead>
                   <tr>
                     <th className={styles.budgetCodeHeader}>बजेट कोड</th>
                     <th className={styles.budgetTitleHeader}>बजेट शिर्षक</th>
                     {(() => {
                      // Generate institution columns dynamically from actual data
                      const institutions = [...new Set(displayData.map(item => item.institution_name).filter(Boolean))];
                      return institutions.map((name, i) => (
                        <th key={i} className={styles.institutionHeader}>
                          {name}
                        </th>
                      ));
                    })()}
                     <th className={styles.totalHeader}>जम्मा</th>
                   </tr>
                 </thead>
                 <tbody>
                   {(() => {
                     // Process actual data from models - Handle nested structure with capital_items and operational_items
                     let allExpenditureItems = [];
                     
                     // Use detailed data if available, otherwise fall back to existing data
                     const dataToUse = detailedData.length > 0 ? detailedData : existingData;
                     
                     // Debug: Log the data structure
                     console.log('dataToUse:', dataToUse);
                     console.log('detailedData:', detailedData);
                     console.log('existingData:', existingData);
                     console.log('displayData:', displayData);
                     console.log('masterItems:', masterItems);
                     
                     // Check if dataToUse is an array of capital expenditure records
                     if (Array.isArray(dataToUse) && dataToUse.length > 0) {
                       // Process each capital expenditure record and combine all expenditure items
                       const allCapitalItems = [];
                       const allOperationalItems = [];
                       
                       dataToUse.forEach(expenditure => {
                         if (expenditure.expenditure_items && Array.isArray(expenditure.expenditure_items)) {
                           // Extract capital items
                           const capitalItems = expenditure.expenditure_items
                             .filter(item => item.item_type === 'capital' || 
                                            (item.code_number && item.code_number.startsWith('01.')))
                             .map(item => ({
                               ...item,
                               institution_name: expenditure.institution_name || selectedOffice?.name || 'केन्द्रीय कार्यालय',
                               item_type: 'capital',
                               total_budget: parseFloat(item.unallocated_budget || item.revised_budget || 0),
                               total_expense: parseFloat(item.q1_expense || 0) + parseFloat(item.q2_expense || 0) + parseFloat(item.q3_expense || 0) + parseFloat(item.q4_expense || 0),
                               q1_budget_demand: parseFloat(item.q1_budget_demand || 0)
                             }));
                           
                           // Extract operational items
                           const operationalItems = expenditure.expenditure_items
                             .filter(item => item.item_type === 'operational' || 
                                            (item.code_number && item.code_number.startsWith('02.')))
                             .map(item => ({
                               ...item,
                               institution_name: expenditure.institution_name || selectedOffice?.name || 'केन्द्रीय कार्यालय',
                               item_type: 'operational',
                               total_budget: parseFloat(item.unallocated_budget || item.revised_budget || 0),
                               total_expense: parseFloat(item.q1_expense || 0) + parseFloat(item.q2_expense || 0) + parseFloat(item.q3_expense || 0) + parseFloat(item.q4_expense || 0),
                               q1_budget_demand: parseFloat(item.q1_budget_demand || 0)
                             }));
                           
                           allCapitalItems.push(...capitalItems);
                           allOperationalItems.push(...operationalItems);
                         }
                       });
                       
                       allExpenditureItems = [...allCapitalItems, ...allOperationalItems];
                       
                       // Debug: Log the processed items
                       console.log('Processed capital items:', allCapitalItems);
                       console.log('Processed operational items:', allOperationalItems);
                       console.log('All expenditure items:', allExpenditureItems);
                     } else if (dataToUse && dataToUse.expenditure_items && Array.isArray(dataToUse.expenditure_items)) {
                       // Single capital expenditure record with expenditure_items structure (from CapitalExpenditureSerializer)
                       const capitalItems = dataToUse.expenditure_items
                         .filter(item => item.item_type === 'capital' || 
                                        (item.code_number && item.code_number.startsWith('01.')))
                         .map(item => ({
                           ...item,
                           institution_name: dataToUse.institution_name || selectedOffice?.name || 'केन्द्रीय कार्यालय',
                           item_type: 'capital',
                           total_budget: parseFloat(item.unallocated_budget || item.revised_budget || 0),
                           total_expense: parseFloat(item.q1_expense || 0) + parseFloat(item.q2_expense || 0) + parseFloat(item.q3_expense || 0) + parseFloat(item.q4_expense || 0),
                           q1_budget_demand: parseFloat(item.q1_budget_demand || 0)
                         }));
                       
                       const operationalItems = dataToUse.expenditure_items
                         .filter(item => item.item_type === 'operational' || 
                                        (item.code_number && item.code_number.startsWith('02.')))
                         .map(item => ({
                           ...item,
                           institution_name: dataToUse.institution_name || selectedOffice?.name || 'केन्द्रीय कार्यालय',
                           item_type: 'operational',
                           total_budget: parseFloat(item.unallocated_budget || item.revised_budget || 0),
                           total_expense: parseFloat(item.q1_expense || 0) + parseFloat(item.q2_expense || 0) + parseFloat(item.q3_expense || 0) + parseFloat(item.q4_expense || 0),
                           q1_budget_demand: parseFloat(item.q1_budget_demand || 0)
                         }));
                       
                       allExpenditureItems = [...capitalItems, ...operationalItems];
                       
                       // Debug: Log the processed items
                       console.log('Processed capital items:', capitalItems);
                       console.log('Processed operational items:', operationalItems);
                       console.log('All expenditure items:', allExpenditureItems);
                     } else if (dataToUse && dataToUse.capital_items && dataToUse.operational_items) {
                       // Legacy structure from get_expenditure_by_fiscal_year API
                       const capitalItems = dataToUse.capital_items.map(item => ({
                         ...item,
                         institution_name: dataToUse.institution_name || selectedOffice?.name || 'केन्द्रीय कार्यालय',
                         item_type: 'capital',
                         total_budget: parseFloat(item.unallocated_budget || item.revised_budget || 0),
                         total_expense: parseFloat(item.q1_expense || 0) + parseFloat(item.q2_expense || 0) + parseFloat(item.q3_expense || 0) + parseFloat(item.q4_expense || 0),
                         q1_budget_demand: parseFloat(item.q1_budget_demand || 0)
                       }));
                       
                       const operationalItems = dataToUse.operational_items.map(item => ({
                         ...item,
                         institution_name: dataToUse.institution_name || selectedOffice?.name || 'केन्द्रीय कार्यालय',
                         item_type: 'operational',
                         total_budget: parseFloat(item.unallocated_budget || item.revised_budget || 0),
                         total_expense: parseFloat(item.q1_expense || 0) + parseFloat(item.q2_expense || 0) + parseFloat(item.q3_expense || 0) + parseFloat(item.q4_expense || 0),
                         q1_budget_demand: parseFloat(item.q1_budget_demand || 0)
                       }));
                       
                       allExpenditureItems = [...capitalItems, ...operationalItems];
                       
                       // Debug: Log the processed items
                       console.log('Processed capital items:', capitalItems);
                       console.log('Processed operational items:', operationalItems);
                       console.log('All expenditure items:', allExpenditureItems);
                     } else {
                       // Fallback to displayData if no nested structure
                       allExpenditureItems = displayData;
                       console.log('Using displayData fallback:', allExpenditureItems);
                     }
                     
                     // Generate institution columns from actual data
                     const institutions = [...new Set(allExpenditureItems.map(item => item.institution_name).filter(Boolean))];
                     
                     // Group data by institution
                     const institutionData = {};
                     institutions.forEach(institution => {
                       institutionData[institution] = {
                         totalBudget: 0,
                         totalExpense: 0,
                         items: []
                       };
                     });
                     
                     // Process each expenditure item
                     allExpenditureItems.forEach(item => {
                       const institution = item.institution_name;
                       if (institution && institutionData[institution]) {
                         // Use the original budget fields from the item
                         const budget = parseFloat(item.unallocated_budget || item.revised_budget || item.budget || item.total_budget || 0);
                         const expense = parseFloat(item.total_expense || 0);
                         
                         institutionData[institution].totalBudget += budget;
                         institutionData[institution].totalExpense += expense;
                         
                         institutionData[institution].items.push({
                           id: item.id,
                           budget: budget,
                           expense: expense,
                           status: item.status || 'draft',
                           fiscalYear: item.fiscal_year_display || 'N/A',
                           createdAt: item.created_at || new Date().toISOString(),
                           // Use actual data from the ExpenditureItem model
                           budgetCode: item.code_number || item.code || item.budget_code || item.id,
                           expenditureTitle: item.expense_title || item.title || item.item_name || `${institution} - ${item.fiscal_year_display || 'N/A'}`,
                           codeNumber: item.code_number || item.code || item.id,
                           // Keep original budget fields for calculations
                           unallocated_budget: item.unallocated_budget,
                           revised_budget: item.revised_budget,
                           item_type: item.item_type,
                           // Use the exact q1_budget_demand field from database - this is the key field for बजेट माग
                           q1_budget_demand: parseFloat(item.q1_budget_demand || 0)
                         });
                       }
                     });
                     
                     // Calculate totals
                     const totalBudget = Object.values(institutionData).reduce((sum, data) => sum + data.totalBudget, 0);
                     const totalExpense = Object.values(institutionData).reduce((sum, data) => sum + data.totalExpense, 0);
                     
                     // Debug: Log the processed institution data
                     console.log('Institution Data:', institutionData);
                     console.log('Institutions:', institutions);
                     
                     // Debug: Log q1_budget_demand values for verification
                     allExpenditureItems.forEach(item => {
                       console.log(`Item ${item.code_number}: q1_budget_demand = ${item.q1_budget_demand}`);
                     });
                     
                     return (
                       <>
                         {/* पुँजीगत खर्च जम्मा Row - Show first */}
                         <tr className={styles.subtotalRow}>
                           <td className={styles.budgetCode}></td>
                           <td className={styles.budgetTitle} style={{ textAlign: 'left' }}>पुँजीगत खर्च जम्मा</td>
                           {institutions.map((institution, index) => {
                             // Calculate total capital expenditure budget demand for this institution from नयाँ डाटा थप्नुहोस्
                             let totalCapitalBudget = 0;
                             
                             if (institutionData[institution] && institutionData[institution].items) {
                               // Sum only capital expenditure items' budget demand for this institution
                               institutionData[institution].items.forEach(item => {
                                 // Check if this is a capital expenditure item
                                 if (item.item_type === 'capital' || 
                                     (item.budgetCode && typeof item.budgetCode === 'string' && item.budgetCode.startsWith('01.')) ||
                                     (item.codeNumber && typeof item.codeNumber === 'string' && item.codeNumber.startsWith('01.'))) {
                                   const budgetDemand = parseFloat(item.q1_budget_demand || 0);
                                   totalCapitalBudget += budgetDemand;
                                 }
                               });
                             }
                             
                             return (
                               <td key={index} className={styles.institutionValue}>
                                 {totalCapitalBudget > 0 ? totalCapitalBudget.toLocaleString('en-IN') : ''}
                               </td>
                             );
                           })}
                           <td className={styles.totalValue}>
                             {(() => {
                               // Calculate grand total across all institutions for capital expenditure only
                               let grandTotal = 0;
                               institutions.forEach(institution => {
                                 if (institutionData[institution] && institutionData[institution].items) {
                                   institutionData[institution].items.forEach(item => {
                                     // Check if this is a capital expenditure item
                                     if (item.item_type === 'capital' || 
                                         (item.budgetCode && typeof item.budgetCode === 'string' && item.budgetCode.startsWith('01.')) ||
                                         (item.codeNumber && typeof item.codeNumber === 'string' && item.codeNumber.startsWith('01.'))) {
                                       const budgetDemand = parseFloat(item.q1_budget_demand || 0);
                                       grandTotal += budgetDemand;
                                     }
                                   });
                                 }
                               });
                               return grandTotal > 0 ? grandTotal.toLocaleString('en-IN') : '';
                             })()}
                           </td>
                         </tr>

                         {/* Unique Budget Codes from Actual Data - Capital Items */}
                         {(() => {
                           // Get unique budget codes from all capital items
                           const uniqueBudgetCodes = new Map();
                           
                           allExpenditureItems
                             .filter(item => item.item_type === 'capital' || 
                                            (item.code_number && item.code_number.startsWith('01.')))
                             .forEach(item => {
                               const code = item.code_number || item.budgetCode || item.code || item.id;
                               const title = item.expense_title || item.expenditureTitle || item.title || 'N/A';
                               
                               if (!uniqueBudgetCodes.has(code)) {
                                 uniqueBudgetCodes.set(code, {
                                   code: code,
                                   title: title,
                                   items: []
                                 });
                               }
                               uniqueBudgetCodes.get(code).items.push(item);
                             });
                           
                           return Array.from(uniqueBudgetCodes.values()).map((budgetCodeData, index) => {
                             return (
                               <tr key={`capital-${budgetCodeData.code}`} className={styles.dataRow}>
                                 <td className={styles.budgetCode}>{budgetCodeData.code}</td>
                                 <td className={styles.budgetTitle}>{budgetCodeData.title}</td>
                                 {institutions.map((institution, instIndex) => {
                                   // Find items for this institution and budget code
                                   const institutionItems = budgetCodeData.items.filter(item => 
                                     item.institution_name === institution
                                   );
                                   
                                   // Calculate total budget demand for this institution and budget code
                                   const budgetValue = institutionItems.reduce((sum, item) => {
                                     const budgetDemand = parseFloat(item.q1_budget_demand || 0);
                                     return sum + budgetDemand;
                                   }, 0);
                                   
                                   return (
                                     <td key={instIndex} className={styles.institutionValue}>
                                       {budgetValue > 0 ? budgetValue.toLocaleString('en-IN') : ''}
                                     </td>
                                   );
                                 })}
                                 <td className={styles.totalValue}>
                                   {(() => {
                                     const totalValue = budgetCodeData.items.reduce((sum, item) => {
                                       const budgetDemand = parseFloat(item.q1_budget_demand || 0);
                                       return sum + budgetDemand;
                                   }, 0);
                                     return totalValue > 0 ? totalValue.toLocaleString('en-IN') : '';
                                   })()}
                                 </td>
                               </tr>
                             );
                           });
                         })()}

                         {/* Operational Expenditure Section */}
                         <tr className={styles.sectionHeaderRow}>
                           <td colSpan={institutions.length + 2} className={styles.sectionHeader}>
                             <strong>संचालन खर्च जम्मा</strong>
                           </td>
                         </tr>
                         
                         {/* संचालन खर्च जम्मा Row - Show total operational expenditure */}
                         <tr className={styles.subtotalRow}>
                           <td className={styles.budgetCode}></td>
                           <td className={styles.budgetTitle}>संचालन खर्च जम्मा</td>
                           {institutions.map((institution, index) => {
                             // Calculate total operational expenditure budget demand for this institution
                             let totalOperationalBudget = 0;
                             
                             if (institutionData[institution] && institutionData[institution].items) {
                               // Sum only operational expenditure items' budget demand for this institution
                               institutionData[institution].items.forEach(item => {
                                 // Check if this is an operational expenditure item
                                 if (item.item_type === 'operational' || 
                                     (item.budgetCode && typeof item.budgetCode === 'string' && item.budgetCode.startsWith('02.')) ||
                                     (item.codeNumber && typeof item.codeNumber === 'string' && item.codeNumber.startsWith('02.'))) {
                                   const budgetDemand = parseFloat(item.q1_budget_demand || 0);
                                   totalOperationalBudget += budgetDemand;
                                 }
                               });
                             }
                             
                             return (
                               <td key={index} className={styles.institutionValue}>
                                 {totalOperationalBudget > 0 ? totalOperationalBudget.toLocaleString('en-IN') : ''}
                               </td>
                             );
                           })}
                           <td className={styles.totalValue}>
                             {(() => {
                               // Calculate grand total across all institutions for operational expenditure only
                               let grandTotal = 0;
                               institutions.forEach(institution => {
                                 if (institutionData[institution] && institutionData[institution].items) {
                                   institutionData[institution].items.forEach(item => {
                                     // Check if this is an operational expenditure item
                                     if (item.item_type === 'operational' || 
                                         (item.budgetCode && typeof item.budgetCode === 'string' && item.budgetCode.startsWith('02.')) ||
                                         (item.codeNumber && typeof item.codeNumber === 'string' && item.codeNumber.startsWith('02.'))) {
                                       const budgetDemand = parseFloat(item.q1_budget_demand || 0);
                                       grandTotal += budgetDemand;
                                     }
                                   });
                                 }
                               });
                               return grandTotal > 0 ? grandTotal.toLocaleString('en-IN') : '';
                             })()}
                           </td>
                         </tr>
                         
                         {/* Unique Budget Codes from Actual Data - Operational Items */}
                         {(() => {
                           // Get unique budget codes from all operational items
                           const uniqueBudgetCodes = new Map();
                           
                           allExpenditureItems
                             .filter(item => (item.item_type === 'operational' || 
                                            (item.code_number && item.code_number.startsWith('02.'))) &&
                                            !(item.code_number && item.code_number.startsWith('01.')))
                             .forEach(item => {
                               const code = item.code_number || item.budgetCode || item.code || item.id;
                               const title = item.expense_title || item.expenditureTitle || item.title || 'N/A';
                               
                               if (!uniqueBudgetCodes.has(code)) {
                                 uniqueBudgetCodes.set(code, {
                                   code: code,
                                   title: title,
                                   items: []
                                 });
                               }
                               uniqueBudgetCodes.get(code).items.push(item);
                             });
                           
                           return Array.from(uniqueBudgetCodes.values()).map((budgetCodeData, index) => {
                             return (
                               <tr key={`operational-${budgetCodeData.code}`} className={styles.dataRow}>
                                 <td className={styles.budgetCode}>{budgetCodeData.code}</td>
                                 <td className={styles.budgetTitle}>{budgetCodeData.title}</td>
                                 {institutions.map((institution, instIndex) => {
                                   // Find items for this institution and budget code
                                   const institutionItems = budgetCodeData.items.filter(item => 
                                     item.institution_name === institution
                                   );
                                   
                                   // Calculate total budget demand for this institution and budget code
                                   const budgetValue = institutionItems.reduce((sum, item) => {
                                     const budgetDemand = parseFloat(item.q1_budget_demand || 0);
                                     return sum + budgetDemand;
                                   }, 0);
                                   
                                   return (
                                     <td key={instIndex} className={styles.institutionValue}>
                                       {budgetValue > 0 ? budgetValue.toLocaleString('en-IN') : ''}
                                     </td>
                                   );
                                 })}
                                 <td className={styles.totalValue}>
                                   {(() => {
                                     const totalValue = budgetCodeData.items.reduce((sum, item) => {
                                       const budgetDemand = parseFloat(item.q1_budget_demand || 0);
                                       return sum + budgetDemand;
                                   }, 0);
                                     return totalValue > 0 ? totalValue.toLocaleString('en-IN') : '';
                                   })()}
                                 </td>
                               </tr>
                             );
                           });
                         })()}
                         
                         {/* Grand Total Row */}
                         <tr className={styles.grandTotalRow}>
                           <td className={styles.budgetCode}></td>
                           <td className={styles.budgetTitle}><strong>जम्मा खर्च</strong></td>
                           {institutions.map((institution, index) => {
                             // Calculate total q1_budget_demand for this institution from all items
                             let totalBudgetDemand = 0;
                             
                             if (institutionData[institution] && institutionData[institution].items) {
                               institutionData[institution].items.forEach(item => {
                                 const budgetDemand = parseFloat(item.q1_budget_demand || 0);
                                 totalBudgetDemand += budgetDemand;
                               });
                             }
                             
                             return (
                               <td key={index} className={styles.institutionValue}>
                                 <strong>{totalBudgetDemand > 0 ? totalBudgetDemand.toLocaleString('en-IN') : ''}</strong>
                               </td>
                             );
                           })}
                           <td className={styles.totalValue}>
                             <strong>
                               {(() => {
                                 // Calculate grand total across all institutions and all items
                                 let grandTotal = 0;
                                 institutions.forEach(institution => {
                                   if (institutionData[institution] && institutionData[institution].items) {
                                     institutionData[institution].items.forEach(item => {
                                       const budgetDemand = parseFloat(item.q1_budget_demand || 0);
                                       grandTotal += budgetDemand;
                                     });
                                   }
                                 });
                                 return grandTotal > 0 ? grandTotal.toLocaleString('en-IN') : '';
                               })()}
                             </strong>
                           </td>
                         </tr>
                       </>
                     );
                   })()}
                 </tbody>
               </table>
             </div>
             )}
           </div>
         </div>
       )}

      {/* Help Section */}
      {showHelp && (
        <div className={styles.helpSection}>
          <div className={styles.helpHeader}>
            <h3>ℹ️ सहायता</h3>
            <p>सिस्टम प्रयोग गर्ने तरिका र सुविधाहरूको जानकारी</p>
          </div>

          <div className={styles.helpContent}>
            <div className={styles.helpCard}>
              <h4>📊 Excel डाउनलोड सुविधा</h4>
              <div className={styles.helpDetails}>
                <p><strong>व्यक्तिगत रेकर्ड डाउनलोड:</strong></p>
                <ul>
                  <li>📊 डाटा हेर्नुहोस् बटन प्रत्येक रेकर्डमा छ</li>
                  <li>त्यो बटन क्लिक गरेर तपाईंले त्यो रेकर्डको Excel फाइल डाउनलोड गर्न सक्नुहुन्छ</li>
                </ul>
                
                <p><strong>फिल्टर गरिएको डाटा डाउनलोड:</strong></p>
                <ul>
                  <li>फिल्टर गरिएको डाटा डाउनलोड बटनले हालको फिल्टर अनुसार सबै रेकर्डहरू एकैपटकमा डाउनलोड गर्नुहुन्छ</li>
                </ul>
              </div>
            </div>

            <div className={styles.helpCard}>
              <h4>🔍 फिल्टर प्रयोग गर्ने तरिका</h4>
              <div className={styles.helpDetails}>
                <ul>
                  <li><strong>संस्था:</strong> संस्था नाम अनुसार खोज्नुहोस्</li>
                  <li><strong>आर्थिक वर्ष:</strong> माथिको चालु वर्ष अनुसार डाटा देखिन्छ; एड्मिनले माथिबाट अघिल्लो वर्ष छान्न सक्नुहुन्छ</li>
                  <li><strong>मिति बाट/सम्म:</strong> मिति अनुसार डाटा फिल्टर गर्नुहोस्</li>
                  <li><strong>खर्च प्रकार:</strong> पुँजीगत वा संचालन खर्च छान्नुहोस्</li>
                  <li><strong>बजेट रेन्ज:</strong> न्यूनतम र अधिकतम बजेट अनुसार फिल्टर गर्नुहोस्</li>
                  <li><strong>स्थिति:</strong> मस्यौदा, पेश गरिएको, मन्जुर, अस्वीकृत, पूर्ण अनुसार फिल्टर गर्नुहोस्</li>
                </ul>
              </div>
            </div>

            <div className={styles.helpCard}>
              <h4>📋 रिपोर्टहरू</h4>
              <div className={styles.helpDetails}>
                <ul>
                  <li><strong>सारांश रिपोर्ट:</strong> सबै खर्च आइटमहरूको समग्र विवरण र सारांश</li>
                  <li><strong>विस्तृत रिपोर्ट:</strong> Summary.xlsx जस्तै फरमाटमा खर्च आइटमहरूको विस्तृत विवरण</li>
                </ul>
              </div>
            </div>

            <div className={styles.helpCard}>
              <h4>🗑️ बल्क डिलिट</h4>
              <div className={styles.helpDetails}>
                <ul>
                  <li>एक वा बढी रेकर्डहरू छान्नुहोस्</li>
                  <li>बल्क डिलिट बटन क्लिक गर्नुहोस्</li>
                  <li>पुष्टि गर्नुहोस् र रेकर्डहरू मेटाउनुहोस्</li>
                </ul>
              </div>
            </div>

            <div className={styles.helpCard}>
              <h4>📝 डाटा थप्ने तरिका</h4>
              <div className={styles.helpDetails}>
                <ol>
                  <li>"नयाँ डाटा थप्नुहोस्" ट्याबमा जानुहोस्</li>
                  <li>कार्यालय छान्नुहोस् (आर्थिक वर्ष चालु वर्षमा स्वतः सेट हुन्छ)</li>
                  <li>खर्च आइटमहरू भर्नुहोस्</li>
                  <li>सेभ गर्नुहोस्</li>
                  <li>फिर्ता "डाटा हेर्नुहोस्" ट्याबमा आउनुहोस्</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Data Table */}
      {!showSummaryReport && !showDetailedReport && !showHelp && !showQ1Report && !showQ2Report && !showQ3Report && !showQ4Report && !showBudgetReport && !showAnnualTotalsReport && (
        <div className={styles.dataTableContainer}>
          {loading ? (
            <div className={styles.loading}>लोड हुँदैछ...</div>
          ) : displayData.length === 0 ? (
            <div className={styles.noData}>
              <div className={styles.noDataContent}>
                <h3>📋 कुनै सेभ गरिएको डाटा फेला परेन</h3>
                <p>यहाँ देखिने डाटा "नयाँ डाटा थप्नुहोस्" ट्याबमा थपिएको डाटा हो।</p>
                
                <div className={styles.noDataActions}>
                  <button 
                    className={styles.primaryButton}
                    onClick={onRefreshData}
                    disabled={loading}
                  >
                    {loading ? 'लोड हुँदैछ...' : '🔄 डाटा रिफ्रेश गर्नुहोस्'}
                  </button>
                  
                  <div className={styles.noDataInstructions}>
                    <h4>डाटा थप्नको लागि:</h4>
                    <ol>
                      <li>"नयाँ डाटा थप्नुहोस्" ट्याबमा जानुहोस्</li>
                      <li>कार्यालय छान्नुहोस् (आर्थिक वर्ष चालु वर्षमा स्वतः सेट हुन्छ)</li>
                      <li>खर्च आइटमहरू भर्नुहोस्</li>
                      <li>सेभ गर्नुहोस्</li>
                      <li>फिर्ता "डाटा हेर्नुहोस्" ट्याबमा आउनुहोस्</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <table className={styles.dataTable}>
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      title="सबै छान्नुहोस्"
                    />
                  </th>
                  <th>संस्था</th>
                  <th>आर्थिक वर्ष</th>
                  <th>सिर्जना मिति</th>
                  <th>अन्तिम अपडेट</th>
                  <th>स्थिति</th>
                  <th>कार्यहरू</th>
                </tr>
              </thead>
              <tbody>
                {displayData.map((expenditure) => (
                  <tr key={expenditure.id} className={styles.dataRow}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedItems.includes(expenditure.id)}
                        onChange={(e) => handleItemSelect(expenditure.id, e.target.checked)}
                        title="यो आइटम छान्नुहोस्"
                      />
                    </td>
                    <td>{expenditure.institution_name || 'N/A'}</td>
                    <td>{expenditure.fiscal_year_display || 'N/A'}</td>
                    <td>{(() => {
                      try {
                        const date = new Date(expenditure.created_at);
                        return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString('ne-NP');
                      } catch (error) {
                        console.error('Error formatting created_at date:', expenditure.created_at, error);
                        return 'N/A';
                      }
                    })()}</td>
                    <td>{(() => {
                      try {
                        const date = new Date(expenditure.updated_at);
                        return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString('ne-NP');
                      } catch (error) {
                        console.error('Error formatting updated_at date:', expenditure.updated_at, error);
                        return 'N/A';
                      }
                    })()}</td>
                    <td>
                      <span className={`${styles.statusBadge} ${styles[expenditure.status || 'draft']}`}>
                        {expenditure.status === 'draft' ? 'मस्यौदा' : 
                         expenditure.status === 'submitted' ? 'पेश गरिएको' :
                         expenditure.status === 'approved' ? 'मन्जुर' :
                         expenditure.status === 'rejected' ? 'अस्वीकृत' :
                         expenditure.status === 'completed' ? 'पूर्ण' : expenditure.status || 'मस्यौदा'}
                      </span>
                    </td>
                    <td className={styles.actionButtons}>
                      <button
                        className={styles.editButton}
                        onClick={() => handleEdit(expenditure)}
                      >
                        सम्पादन गर्नुहोस्
                      </button>
                      
                      <button
                        className={styles.deleteButton}
                        onClick={() => handleDelete(expenditure)}
                        title="यो रेकर्ड मेटाउनुहोस्"
                      >
                        मेटाउनुहोस्
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}


      {/* Edit Form Modal */}
      {selectedDataForEdit && (
        <div className={styles.editModal}>
          <div className={styles.editModalContent}>
            <div className={styles.editModalHeader}>
              <h3>{viewMode ? 'डाटा विवरण हेर्नुहोस्' : 'डाटा सम्पादन गर्नुहोस्'}</h3>
              <button
                className={styles.closeButton}
                onClick={() => {
                  setSelectedDataForEdit(null);
                  setViewMode(false);
                }}
              >
                ×
              </button>
            </div>
            
            <div className={styles.editModalBody}>
              <div className={styles.editForm}>
                {viewMode ? (
                  // Read-only view mode
                  <>
                    <div className={styles.formRow}>
                      <label><strong>संस्था:</strong></label>
                      <span>{selectedDataForEdit.institution_name || 'N/A'}</span>
                    </div>
                    
                    <div className={styles.formRow}>
                      <label><strong>आर्थिक वर्ष:</strong></label>
                      <span>{selectedDataForEdit.fiscal_year_display || 'N/A'}</span>
                    </div>
                    
                    <div className={styles.formRow}>
                      <label><strong>टोकन:</strong></label>
                      <span>{selectedDataForEdit.token || 'N/A'}</span>
                    </div>
                    
                    <div className={styles.formRow}>
                      <label><strong>स्थिति:</strong></label>
                      <span>{selectedDataForEdit.status || 'N/A'}</span>
                    </div>
                    
                    <div className={styles.formRow}>
                      <label><strong>सिर्जना मिति:</strong></label>
                      <span>{(() => {
                        try {
                          const date = new Date(selectedDataForEdit.created_at);
                          return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString('ne-NP');
                        } catch (error) {
                          console.error('Error formatting created_at date in modal:', selectedDataForEdit.created_at, error);
                          return 'N/A';
                        }
                      })()}</span>
                    </div>
                    
                    <div className={styles.formRow}>
                      <label><strong>अन्तिम अपडेट:</strong></label>
                      <span>{(() => {
                        try {
                          const date = new Date(selectedDataForEdit.updated_at);
                          return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString('ne-NP');
                        } catch (error) {
                          console.error('Error formatting updated_at date in modal:', selectedDataForEdit.updated_at, error);
                          return 'N/A';
                        }
                      })()}</span>
                    </div>
                  </>
                ) : (
                  // Editable form mode
                  <>
                    <div className={styles.formRow}>
                      <label><strong>संस्था:</strong></label>
                      <input
                        type="text"
                        value={selectedDataForEdit.institution_name || ''}
                        onChange={(e) => setSelectedDataForEdit({
                          ...selectedDataForEdit,
                          institution_name: e.target.value
                        })}
                        className={styles.formInput}
                        placeholder="संस्था नाम भर्नुहोस्"
                      />
                    </div>
                    
                    <div className={styles.formRow}>
                      <label><strong>आर्थिक वर्ष:</strong></label>
                      <input
                        type="text"
                        value={selectedDataForEdit.fiscal_year_display || ''}
                        onChange={(e) => setSelectedDataForEdit({
                          ...selectedDataForEdit,
                          fiscal_year_display: e.target.value
                        })}
                        className={styles.formInput}
                        placeholder="आर्थिक वर्ष भर्नुहोस्"
                      />
                    </div>
                    
                    <div className={styles.formRow}>
                      <label><strong>टोकन:</strong></label>
                      <input
                        type="text"
                        value={selectedDataForEdit.token || ''}
                        onChange={(e) => setSelectedDataForEdit({
                          ...selectedDataForEdit,
                          token: e.target.value
                        })}
                        className={styles.formInput}
                        placeholder="टोकन भर्नुहोस्"
                      />
                    </div>
                    
                    <div className={styles.formRow}>
                      <label><strong>स्थिति:</strong></label>
                      <select
                        value={selectedDataForEdit.status || 'draft'}
                        onChange={(e) => setSelectedDataForEdit({
                          ...selectedDataForEdit,
                          status: e.target.value
                        })}
                        className={styles.formSelect}
                      >
                        <option value="draft">मस्यौदा</option>
                        <option value="submitted">पेश गरिएको</option>
                        <option value="approved">मन्जुर</option>
                        <option value="rejected">अस्वीकृत</option>
                        <option value="completed">पूर्ण</option>
                      </select>
                    </div>
                    
                    <div className={styles.formRow}>
                      <label><strong>सिर्जना मिति:</strong></label>
                      <span>{(() => {
                        try {
                          const date = new Date(selectedDataForEdit.created_at);
                          return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString('ne-NP');
                        } catch (error) {
                          console.error('Error formatting created_at date in modal:', selectedDataForEdit.created_at, error);
                          return 'N/A';
                        }
                      })()}</span>
                    </div>
                    
                    <div className={styles.formRow}>
                      <label><strong>अन्तिम अपडेट:</strong></label>
                      <span>{(() => {
                        try {
                          const date = new Date(selectedDataForEdit.updated_at);
                          return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString('ne-NP');
                        } catch (error) {
                          console.error('Error formatting updated_at date in modal:', selectedDataForEdit.updated_at, error);
                          return 'N/A';
                        }
                      })()}</span>
                    </div>
                  </>
                )}
              </div>
              
              <div className={styles.editActions}>
                {!viewMode && (
                  <button
                    className={styles.updateButton}
                    onClick={handleUpdate}
                    disabled={loading}
                  >
                    {loading ? 'अपडेट हुँदैछ...' : 'अपडेट गर्नुहोस्'}
                  </button>
                )}
                
                <button
                  className={styles.cancelButton}
                  onClick={() => {
                    setSelectedDataForEdit(null);
                    setViewMode(false);
                  }}
                >
                  {viewMode ? 'बन्द गर्नुहोस्' : 'रद्द गर्नुहोस्'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* No Data Message */}
      {!loading && (displayData.length === 0) && (
        <div className={styles.noDataMessage}>
          <p>कुनै डाटा फेला परेन। कृपया पहिले केही डाटा थप्नुहोस्।</p>
        </div>
      )}
    </div>
  );
};

export default CapitalExpenditureDataView;
